import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE, pageMeta, schemaFor } from "@/lib/seo";

/**
 * Apply the current route's metadata to the document.
 *
 * Client-side only, which covers Googlebot (it renders JavaScript) and every
 * in-app navigation. It does NOT cover the crawlers behind link previews —
 * Slack, LinkedIn, iMessage, most AI fetchers — because those read the raw
 * HTML response and never execute a script. Those are served by
 * scripts/prerender-seo.js, which bakes the same table into static HTML at
 * build time. Two mechanisms, one source of truth, so they cannot disagree.
 *
 * Everything it writes is tagged data-seo, and every run clears the previous
 * tags first: on a client-side route change the old page's description would
 * otherwise still be in the head, which is worse than having none.
 */
const MANAGED = "data-seo";

function setTag(selectorAttrs, content) {
  const [attr, value] = selectorAttrs;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.setAttribute(MANAGED, "");
}

export default function useSeo(extraSchema) {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = pageMeta(pathname);
    const url = `${SITE.url}${pathname === "/" ? "" : pathname}`;

    document.title = meta.title;
    setTag(["name", "description"], meta.description);
    setTag(["property", "og:title"], meta.title);
    setTag(["property", "og:description"], meta.description);
    setTag(["property", "og:url"], url);
    setTag(["name", "twitter:title"], meta.title);
    setTag(["name", "twitter:description"], meta.description);
    // Sign-in and sign-up are real pages with nothing to rank for; leaving
    // them indexable spends crawl budget and competes with the pages that do.
    setTag(["name", "robots"], meta.noindex ? "noindex, follow" : "index, follow");

    // Self-referencing canonical. Without one, ?utm_source= and a trailing
    // slash are three URLs for one page as far as Search Console is concerned.
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);

    // Replaced wholesale each time rather than appended to — otherwise
    // navigating A -> B -> A leaves three copies in the head.
    document.head
      .querySelectorAll(`script[type="application/ld+json"][${MANAGED}]`)
      .forEach((n) => n.remove());
    const blocks = [...schemaFor(pathname), ...(extraSchema ? [extraSchema] : [])];
    blocks.forEach((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(MANAGED, "");
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    });
  }, [pathname, extraSchema]);
}
